import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import PageCrossLink from "@/components/PageCrossLink";
import SimulatorStatusBadge from "@/components/SimulatorStatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Copy,
  RefreshCw,
  Download,
  Cpu,
  Key,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Droplets,
  Save,
  Play,
  Square,
  Wand2,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

export const WIZARD = {
  rotateButton: "wizard-rotate-key",
  copyKeyButton: "wizard-copy-key",
  downloadSketchButton: "wizard-download-sketch",
  simulatorSwitch: "wizard-simulator-switch",
  calibSave: "wizard-calib-save",
};

function SamplingControl({ sensorId, onApplied }) {
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("wet");
  const [duration, setDuration] = useState(20);
  const pollRef = React.useRef(null);

  const poll = async () => {
    try {
      const { data } = await api.get(`/sensors/${sensorId}/calibration/session`);
      setSession(data.session);
      if (data.session && data.session.status !== "running") {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    poll();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensorId]);

  const start = async () => {
    try {
      await api.post(`/sensors/${sensorId}/calibration/session/start`, { phase, duration_seconds: Number(duration) });
      toast.success(`Sampling ${phase.toUpperCase()} dimulai — celupkan probe & tunggu ${duration}s`);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(poll, 1500);
      poll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal start sampling");
    }
  };

  const finish = async () => {
    try {
      const { data } = await api.post(`/sensors/${sensorId}/calibration/session/finish`);
      setSession(data);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      toast.success("Sampling selesai");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal finish");
    }
  };

  const apply = async () => {
    try {
      await api.post(`/sensors/${sensorId}/calibration/session/apply`);
      toast.success(`Nilai ${session.phase.toUpperCase()} = ${session.auto_value} di-apply ke kalibrasi`);
      onApplied && onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal apply");
    }
  };

  const isRunning = session?.status === "running";
  const isCompleted = session?.status === "completed";
  const samples = session?.samples || [];
  const pct = isRunning ? Math.min(100, ((session.elapsed_seconds || 0) / (session.duration_seconds || 1)) * 100) : (isCompleted ? 100 : 0);

  return (
    <div className="w-full mt-3 p-3 border border-dashed border-border rounded-md bg-muted/30 space-y-3">
      <div className="flex items-center gap-2 text-xs">
        <Wand2 className="w-3.5 h-3.5 text-primary" />
        <span className="font-medium">Sampling Wizard</span>
        <span className="text-muted-foreground">— alih-alih ketik manual, celup probe & klik Start</span>
      </div>

      {!isRunning && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider">Fase</Label>
            <Select value={phase} onValueChange={setPhase}>
              <SelectTrigger className="h-8 w-32 text-xs" data-testid={`sampling-phase-${sensorId}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="wet">WET (basah)</SelectItem>
                <SelectItem value="dry">DRY (kering)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider">Durasi (detik)</Label>
            <Input type="number" min={5} max={300} value={duration} onChange={(e) => setDuration(e.target.value)} className="h-8 w-24 text-xs" />
          </div>
          <Button size="sm" onClick={start} data-testid={`sampling-start-${sensorId}`}>
            <Play className="w-3.5 h-3.5 mr-1" /> Start
          </Button>
        </div>
      )}

      {isRunning && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span><span className="status-dot bg-primary inline-block mr-1" /> Sampling {session.phase.toUpperCase()}…</span>
            <span className="font-mono">{samples.length} sample · sisa {session.remaining_seconds ?? 0}s</span>
          </div>
          <Progress value={pct} className="h-2" />
          <Button size="sm" variant="outline" onClick={finish} data-testid={`sampling-stop-${sensorId}`}>
            <Square className="w-3.5 h-3.5 mr-1" /> Stop & finalisasi
          </Button>
          <p className="text-[10px] text-muted-foreground">ESP32 push raw ADC via <code className="font-mono">POST /sensors/{sensorId.slice(0,8)}…/calibration/session/sample</code>. Demo: simulator otomatis inject sample sintetis.</p>
        </div>
      )}

      {isCompleted && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge className="bg-primary/10 text-primary border-primary/20">SELESAI · {session.phase.toUpperCase()}</Badge>
            <span>Auto value (<strong>{session.phase === "wet" ? "MIN" : "MAX"}</strong>): <code className="font-mono text-foreground font-semibold">{session.auto_value}</code></span>
            <span className="text-muted-foreground">Avg: {session.avg_value?.toFixed(0)} · {samples.length} sample</span>
          </div>
          {samples.length > 0 && (
            <div className="flex gap-0.5 h-6 items-end">
              {samples.slice(-40).map((v, i) => {
                const norm = Math.max(2, Math.min(100, (v / 4095) * 100));
                return <div key={i} title={v} style={{ height: `${norm}%`, width: "6px" }} className="bg-primary/40 rounded-sm" />;
              })}
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={apply} data-testid={`sampling-apply-${sensorId}`}>
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Apply ke {session.phase.toUpperCase()}
            </Button>
            <Button size="sm" variant="outline" onClick={start}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Sampling ulang
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CalibrationPanel({ projectId, sensors }) {
  const soil = sensors.filter((s) => s.type === "soil_moisture");
  const [cals, setCals] = useState({}); // sensor_id -> {wet, dry}
  const [saving, setSaving] = useState({});

  const fetch_ = async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/calibration`);
      const map = {};
      data.forEach((c) => { map[c.sensor_id] = { wet: c.moisture_wet_value, dry: c.moisture_dry_value, updated_at: c.updated_at }; });
      setCals(map);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { fetch_(); /* eslint-disable-next-line */ }, [projectId]);

  const setField = (sid, key, value) => {
    setCals((prev) => ({ ...prev, [sid]: { ...(prev[sid] || { wet: 1500, dry: 3500 }), [key]: Number(value) } }));
  };

  const save = async (sid) => {
    const c = cals[sid] || { wet: 1500, dry: 3500 };
    if (c.wet >= c.dry) {
      toast.error("Nilai basah (wet) harus LEBIH KECIL dari nilai kering (dry)");
      return;
    }
    setSaving((p) => ({ ...p, [sid]: true }));
    try {
      await api.put(`/sensors/${sid}/calibration`, {
        moisture_wet_value: c.wet,
        moisture_dry_value: c.dry,
      });
      toast.success("Kalibrasi sensor kelembapan berhasil diperbarui");
      fetch_();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving((p) => ({ ...p, [sid]: false }));
    }
  };

  if (soil.length === 0) {
    return <div className="text-sm text-muted-foreground">Belum ada sensor soil_moisture pada proyek ini.</div>;
  }

  return (
    <div className="space-y-4">
      <Alert>
        <Droplets className="w-4 h-4" />
        <AlertDescription className="text-sm">
          Kalibrasi memetakan nilai ADC raw sensor (0–4095) ke persen kelembapan (0–100%). ESP32 akan hitung: <code className="font-mono">pct = map(raw, DRY, WET, 0, 100)</code>.
        </AlertDescription>
      </Alert>

      <div className="border border-primary/30 bg-primary/5 rounded-lg p-4 space-y-2" data-testid="calibration-howto">
        <div className="flex items-center gap-2 font-medium text-sm">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs">?</span>
          Cara menggunakan Sampling Wizard
        </div>
        <ol className="text-xs text-muted-foreground space-y-1 ml-6 list-decimal">
          <li><strong>Fase WET</strong>: rendam probe sensor ke dalam air / tanah basah jenuh, lalu klik <strong>Start</strong>. Tunggu durasi (default 30 detik) selesai.</li>
          <li>Tanpa ESP32 fisik, sistem otomatis inject sample sintetis demo (1200–1600). Dengan ESP32, sensor push raw ADC via endpoint kalibrasi.</li>
          <li>Klik <strong>Selesai & finalisasi</strong> — sistem hitung nilai MIN (untuk wet) atau MAX (untuk dry) plus rata-rata.</li>
          <li>Klik <strong>Apply</strong> — nilai otomatis tersimpan ke field Wet/Dry di baris ini.</li>
          <li>Ulangi untuk fase <strong>DRY</strong> (probe kering total, di udara terbuka).</li>
          <li>Regenerate sketch ESP32 (tab Sketch → Download) agar nilai kalibrasi baru ikut ke firmware. Flash ulang.</li>
        </ol>
      </div>

      <div className="border border-border rounded-lg bg-card divide-y divide-border">
        {soil.map((s) => {
          const c = cals[s.id] || { wet: 1500, dry: 3500 };
          return (
            <div key={s.id} className="p-4 flex flex-wrap items-end gap-4">
              <div className="flex items-center gap-2 min-w-[200px]">
                <Cpu className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">{s.id.slice(0, 8)}…</div>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Wet (basah) ADC</Label>
                <Input
                  type="number"
                  className="w-28"
                  value={c.wet}
                  onChange={(e) => setField(s.id, "wet", e.target.value)}
                  data-testid={`calib-wet-${s.id}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Dry (kering) ADC</Label>
                <Input
                  type="number"
                  className="w-28"
                  value={c.dry}
                  onChange={(e) => setField(s.id, "dry", e.target.value)}
                  data-testid={`calib-dry-${s.id}`}
                />
              </div>
              <Button size="sm" onClick={() => save(s.id)} disabled={saving[s.id]} data-testid={`calib-save-${s.id}`}>
                <Save className="w-4 h-4 mr-1" /> {saving[s.id] ? "Menyimpan…" : "Simpan"}
              </Button>
              {c.updated_at && (
                <span className="text-[10px] text-muted-foreground ml-auto">{new Date(c.updated_at).toLocaleString()}</span>
              )}
              <SamplingControl sensorId={s.id} onApplied={fetch_} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusDot({ ageSeconds }) {
  let color = "bg-muted-foreground";
  let label = "Belum ada data";
  if (ageSeconds !== null && ageSeconds !== undefined) {
    if (ageSeconds < 15) {
      color = "bg-primary";
      label = `${ageSeconds.toFixed(0)}s lalu`;
    } else if (ageSeconds < 30) {
      color = "bg-accent";
      label = `${ageSeconds.toFixed(0)}s lalu`;
    } else {
      color = "bg-destructive";
      const m = Math.floor(ageSeconds / 60);
      label = m > 0 ? `${m} menit lalu` : `${ageSeconds.toFixed(0)}s lalu`;
    }
  }
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block w-2 h-2 rounded-full ${color}`} />
      <span className="text-xs text-muted-foreground font-mono">{label}</span>
    </div>
  );
}

export default function Wizard() {
  const { current, currentId, fetchProjects } = useProject();
  const [sensors, setSensors] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [rotating, setRotating] = useState(false);
  const [savingSim, setSavingSim] = useState(false);
  // "Opsi B" — when user clicks "Paksa OFF" with this checked, we also POST /water/reset
  // so cumulative water totals (Air Tersedia, Debit Harian) don't retain values from the
  // simulated session and start fresh from ESP32 readings.
  const [resetCumulativeOnOff, setResetCumulativeOnOff] = useState(true);
  const [sketch, setSketch] = useState("");
  const [filename, setFilename] = useState("");

  const fetchSensors = async () => {
    if (!currentId) return;
    const { data } = await api.get(`/projects/${currentId}/sensors`);
    setSensors(data);
  };

  useEffect(() => {
    fetchSensors();
    const id = setInterval(() => {
      fetchSensors();
      setNow(Date.now());
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} disalin`);
    } catch (e) {
      toast.error("Gagal menyalin");
    }
  };

  const rotateKey = async () => {
    if (!window.confirm("Rotasi device key? ESP32 yang sudah memakai key lama akan ditolak.")) return;
    setRotating(true);
    try {
      const { data } = await api.post(`/projects/${currentId}/device-key/rotate`);
      toast.success(`Key baru: ${data.device_key.slice(0, 14)}…`);
      await fetchProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    } finally {
      setRotating(false);
    }
  };

  const setSimulator = async (override) => {
    setSavingSim(true);
    try {
      await api.patch(`/projects/${currentId}/simulator`, { simulate_sensors: override });
      // When switching OFF and the "Reset cumulative" checkbox is on, wipe water_totals
      // so Manajemen Air kembali ke 0 dan ESP32 real bisa mulai dari fresh state.
      let resetDone = false;
      if (override === false && resetCumulativeOnOff) {
        try {
          await api.post(`/projects/${currentId}/water/reset`);
          resetDone = true;
        } catch (e) {
          toast.warning("Simulator OFF berhasil, tapi reset cumulative gagal — cek Manajemen Air.");
        }
      }
      toast.success(
        override === false
          ? (resetDone
              ? "Simulator OFF · Manajemen Air direset ke 0"
              : "Simulator dimatikan untuk proyek ini")
          : override === true
          ? "Simulator dipaksa aktif"
          : "Override dihapus, ikut env default"
      );
      await fetchProjects();
    } catch (e) {
      toast.error("Gagal menyimpan");
    } finally {
      setSavingSim(false);
    }
  };

  const downloadSketch = async () => {
    try {
      const { data } = await api.get(`/projects/${currentId}/esp32-sketch`);
      setSketch(data.sketch);
      setFilename(data.filename);
      // Trigger download
      const blob = new Blob([data.sketch], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Sketch ${data.filename} diunduh`);
    } catch (e) {
      toast.error("Gagal generate sketch");
    }
  };

  if (!currentId) return <div className="text-muted-foreground">Pilih proyek terlebih dulu.</div>;

  const deviceKey = current?.device_key || "";
  const simOverride = current?.simulate_sensors;
  const simEffective = simOverride === null || simOverride === undefined ? "env-default" : simOverride ? "on" : "off";

  const liveSensors = sensors.filter((s) => ["soil_moisture", "flow_in", "flow_out", "pzem"].includes(s.type));
  const upCount = liveSensors.filter((s) => s.last_reading_ts && (now - new Date(s.last_reading_ts).getTime()) / 1000 < 30).length;
  const totalLive = liveSensors.length;

  return (
    <div>
      <PageCrossLink
        from="ESP32 Wizard"
        to={{ label: "Kode Arduino", href: "/arduino", description: "Editor sketch, compile, download .bin, flash via USB" }}
      />
      <PageHeader
        title="ESP32 Setup Wizard"
        overline={current?.name}
        description="Sambungkan ESP32 fisik: dapatkan device key, unduh sketch otomatis, dan pantau status reading masuk."
      />

      {/* Quick status */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="border border-border rounded-lg bg-card p-5">
          <div className="overline text-muted-foreground">Sensor aktif</div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display text-3xl font-bold">{upCount}</span>
            <span className="text-sm text-muted-foreground">/ {totalLive}</span>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">Kirim data dalam 30 detik terakhir</div>
        </div>
        <div className="border border-border rounded-lg bg-card p-5">
          <div className="overline text-muted-foreground">Sumber data</div>
          <div className="mt-2 flex items-center gap-2">
            <Badge variant={simEffective === "off" ? "default" : "outline"} className={simEffective === "off" ? "bg-primary/10 text-primary border-primary/20" : "border-accent text-accent"}>
              {simEffective === "off" ? "ESP32 ONLY" : simEffective === "on" ? "SIMULATOR ON" : "ENV DEFAULT"}
            </Badge>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">Override per proyek di bawah</div>
        </div>
        <div className="border border-border rounded-lg bg-card p-5">
          <div className="overline text-muted-foreground">Endpoint</div>
          <div className="mt-2 font-mono text-xs break-all">{process.env.REACT_APP_BACKEND_URL}/api/readings</div>
          <div className="mt-2 text-xs text-muted-foreground">POST dengan header <span className="font-mono">X-Device-Key</span></div>
        </div>
      </div>

      <Tabs defaultValue="device" className="w-full">
        <TabsList>
          <TabsTrigger value="device">1. Device Key</TabsTrigger>
          <TabsTrigger value="sketch">2. Sketch</TabsTrigger>
          <TabsTrigger value="calibration">3. Kalibrasi</TabsTrigger>
          <TabsTrigger value="monitor">4. Live Monitor</TabsTrigger>
          <TabsTrigger value="simulator">5. Simulator</TabsTrigger>
        </TabsList>

        <TabsContent value="device" className="mt-6">
          <div className="border border-border rounded-lg bg-card p-6 max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                <Key className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="overline text-muted-foreground">Per-project device key</div>
                <h3 className="font-display text-lg font-semibold">Authentication key untuk ESP32</h3>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-muted rounded-md font-mono text-sm break-all">{deviceKey}</code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copy(deviceKey, "Device key")}
                  data-testid={WIZARD.copyKeyButton}
                >
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={rotateKey}
                  disabled={rotating}
                  data-testid={WIZARD.rotateButton}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${rotating ? "animate-spin" : ""}`} /> Rotate
                </Button>
              </div>

              <Alert>
                <AlertDescription className="text-sm">
                  ESP32 wajib mengirim header <span className="font-mono text-foreground">X-Device-Key: {deviceKey.slice(0, 14)}…</span> ketika POST <span className="font-mono text-foreground">/api/readings</span>. Setiap proyek punya key terpisah — bocor satu tidak mempengaruhi yang lain.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="sketch" className="mt-6">
          <div className="border border-border rounded-lg bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline text-muted-foreground">Auto-generated</div>
                <h3 className="font-display text-lg font-semibold">Arduino IDE sketch (.ino)</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Sketch sudah terisi otomatis dengan {liveSensors.length} sensor UUID + device key proyek ini.
                </p>
              </div>
              <Button onClick={downloadSketch} data-testid={WIZARD.downloadSketchButton}>
                <Download className="w-4 h-4 mr-1" /> Download .ino
              </Button>
            </div>
            {sketch && (
              <pre className="bg-muted rounded-md p-4 text-xs font-mono overflow-x-auto max-h-[420px]">
                <div className="text-muted-foreground mb-2">{filename}</div>
                <code>{sketch}</code>
              </pre>
            )}

            <ol className="mt-6 space-y-2 text-sm text-muted-foreground list-decimal list-inside">
              <li>Install <strong>Arduino IDE</strong> + ESP32 board support + library <span className="font-mono text-foreground">ArduinoJson</span>.</li>
              <li>Buka file .ino yang diunduh. Ubah <span className="font-mono text-foreground">WIFI_SSID</span> &amp; <span className="font-mono text-foreground">WIFI_PASS</span>.</li>
              <li>Verifikasi pin <span className="font-mono text-foreground">SOIL_PINS</span>, <span className="font-mono text-foreground">FLOW_IN_PIN</span>, dst sesuai wiring fisik.</li>
              <li>Pilih board <strong>ESP32 Dev Module</strong>, COM port, Upload.</li>
              <li>Buka Serial Monitor (115200) — lihat log "POST sensorId -&gt; 200".</li>
              <li>Pantau tab <strong>Live Monitor</strong> di sebelahnya — sensor dengan badge hijau = data masuk.</li>
            </ol>
          </div>
        </TabsContent>

        <TabsContent value="calibration" className="mt-6">
          <CalibrationPanel projectId={currentId} sensors={sensors} />
        </TabsContent>

        <TabsContent value="monitor" className="mt-6">
          <div className="border border-border rounded-lg bg-card">
            <div className="p-6 border-b border-border flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              <h3 className="font-display text-lg font-semibold">Live sensor monitor</h3>
              <span className="ml-auto text-xs text-muted-foreground">refresh tiap 3 detik</span>
            </div>
            <div className="divide-y divide-border">
              {liveSensors.map((s) => {
                const ageSec = s.last_reading_ts ? (now - new Date(s.last_reading_ts).getTime()) / 1000 : null;
                return (
                  <div key={s.id} className="flex items-center justify-between gap-4 p-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <Cpu className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium truncate">{s.name}</div>
                        <div className="text-xs text-muted-foreground font-mono truncate">{s.id}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="font-display text-lg font-bold">{s.last_reading_value?.toFixed(2) ?? "—"}</div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.unit}</div>
                      </div>
                      <StatusDot ageSeconds={ageSec} />
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Copy sensor ID"
                        onClick={() => copy(s.id, `Sensor ${s.name}`)}
                        data-testid={`copy-sensor-${s.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="simulator" className="mt-6">
          <div className="border border-border rounded-lg bg-card p-6 max-w-2xl space-y-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="overline text-muted-foreground">Override per proyek</div>
                <h3 className="font-display text-lg font-semibold mt-1">Simulator data</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Saat ESP32 fisik sudah mengirim data, matikan simulator agar grafik mencerminkan kondisi sebenarnya.
                  Env var <span className="font-mono text-foreground">SIMULATE_SENSORS</span> jadi default global; override di sini menang.
                </p>
              </div>
              <SimulatorStatusBadge projectId={currentId} />
            </div>

            {/* Live status card — shows resolved effective state for THIS project */}
            <div className="border border-border/70 bg-muted/30 rounded-md p-4 text-xs space-y-1.5" data-testid="wizard-sim-live-status">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status efektif proyek ini</span>
                <SimulatorStatusBadge projectId={currentId} variant="row" />
              </div>
              <div className="text-muted-foreground">
                Env global{" "}
                <span className="font-mono text-foreground">
                  SIMULATE_SENSORS={String(simEffective === "env-default" ? "(tergantung server)" : "override")}
                </span>{" "}
                · Override proyek{" "}
                <span className="font-mono text-foreground">{simOverride === null || simOverride === undefined ? "null (env-default)" : String(simOverride)}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Button
                variant={simEffective === "env-default" ? "default" : "outline"}
                disabled={savingSim}
                onClick={() => setSimulator(null)}
              >
                Env Default
              </Button>
              <Button
                variant={simEffective === "on" ? "default" : "outline"}
                disabled={savingSim}
                onClick={() => setSimulator(true)}
              >
                Paksa ON
              </Button>
              <Button
                variant={simEffective === "off" ? "default" : "outline"}
                disabled={savingSim}
                onClick={() => setSimulator(false)}
                data-testid={WIZARD.simulatorSwitch}
              >
                Paksa OFF (ESP32 only)
              </Button>
            </div>

            {/* Opsi B — reset cumulative water totals when simulator switched OFF */}
            <label
              className="flex items-start gap-2 text-xs cursor-pointer border border-border/70 rounded-md p-3 bg-muted/20 hover:bg-muted/40 transition-colors"
              data-testid="wizard-reset-cumulative-toggle"
            >
              <input
                type="checkbox"
                checked={resetCumulativeOnOff}
                onChange={(e) => setResetCumulativeOnOff(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
                data-testid="wizard-reset-cumulative-checkbox"
              />
              <div className="flex-1">
                <div className="font-medium text-foreground">Reset cumulative saat matikan simulator</div>
                <div className="text-muted-foreground mt-0.5">
                  Otomatis reset Manajemen Air (Air Tersedia, Debit Harian, total in/out) ke 0 saat klik <span className="font-mono">Paksa OFF</span>.
                  Cocok saat beralih dari mode simulasi ke ESP32 fisik agar hitungan mulai dari nol.
                </div>
              </div>
            </label>

            <Alert variant={simEffective === "off" ? "default" : "destructive"}>
              {simEffective === "off" ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              <AlertDescription>
                {simEffective === "off" && "Simulator OFF. Hanya data dari ESP32 yang masuk."}
                {simEffective === "on" && "Simulator FORCE ON. Reading mock + ESP32 tercampur — matikan saat produksi."}
                {simEffective === "env-default" && "Mengikuti env SIMULATE_SENSORS. Kalau env tidak di-set, default aktif. Klik 'Paksa ON' agar tidak bergantung env (recommended untuk deployment)."}
              </AlertDescription>
            </Alert>

            <div className="text-[11px] text-muted-foreground pt-2 border-t border-border/60">
              💡 <span className="font-medium">Tips deployment:</span> Kalau simulator terlihat tidak jalan di production, klik "Paksa ON" — pengaturan tersimpan di DB dan tidak bergantung env server. Untuk mode produksi ESP32 asli, klik "Paksa OFF".
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
